import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/Modal';
import { ModalDelete } from '@/components/ModalDelete';
import { Textarea } from '@/components/Textarea';
import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import { Tabs } from '@/components/Tabs';
import { usePreferenceScope } from '@/hooks/useStoredPreference';
import { opportunityApi, opportunityErrors } from '@/pages/Opportunities/api';
import { OpportunityStatusBadge } from '@/pages/Opportunities/OpportunityStatusBadge';
import { OpportunitySummary } from '@/pages/Opportunities/OpportunitySummary';
import { OpportunityDocuments } from '@/pages/Opportunities/OpportunityDocuments';
import { OpportunityHistory } from '@/pages/Opportunities/OpportunityHistory';
import { OpportunityVersions } from '@/pages/Opportunities/OpportunityVersions';
import { OpportunityTransition } from '@/pages/Opportunities/OpportunityTransition';
import type { OpportunityCatalogs, OpportunityDetail as Detail, OpportunityStatus } from '@/pages/Opportunities/types';

export interface OpportunityDetailProps { id: number; catalogs?: OpportunityCatalogs; onClose: () => void; onEdit: (item: Detail) => void; onSaved: (item: Detail) => void; }
export const OpportunityDetail = ({ id, catalogs, onClose, onEdit, onSaved }: OpportunityDetailProps) => {
    const { t } = useTranslation('global');
    const scope = usePreferenceScope();
    const query = useQuery({ queryKey: ['opportunities', scope, 'detail', id], queryFn: ({ signal }) => opportunityApi.get(id, signal), retry: false });
    const [tab, setTab] = useState('summary');
    const [destination, setDestination] = useState<OpportunityStatus | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [busy, setBusy] = useState(false);
    const [reason, setReason] = useState('');
    const [error, setError] = useState('');
    const item = query.data;
    const remove = async (): Promise<void> => {
        if(!item){
            return;
        }
        setBusy(true); setError('');
        try{
            onSaved(await opportunityApi.remove(id, item.record_version, reason));
            setDeleting(false); onClose();
        }
        catch(failure){
            setError(Object.values(opportunityErrors(failure)).join(' '));
        }
        finally{
            setBusy(false);
        }
    };
    return <Modal isOpen onClose={onClose} title={item ? `${item.code} · ${item.title}` : t('opportunities.detail')} maxWidth="4xl" hideFooter>
        <div className="space-y-5 text-slate-900 dark:text-zinc-100">
            {query.isLoading && <p role="status">{t('opportunities.loading')}</p>}
            {query.isError && <Alert variant="error"><p>{t('opportunities.load_error')}</p><Button variant="outline" onClick={() => { void query.refetch(); }}>{t('opportunities.retry')}</Button></Alert>}
            {item && <>
                <div className="flex flex-wrap items-center gap-2"><OpportunityStatusBadge status={item.workflow_status} />
                    {item.deleted_at && <Alert variant="warning">{t('opportunities.deleted_notice')}</Alert>}
                    {!item.deleted_at && ['decision_pending', 'in_study'].includes(item.workflow_status) && <Button variant="outline" icon="edit" onClick={() => onEdit(item)}>{t('opportunities.edit')}</Button>}
                    {item.allowed_transitions?.map(status => <Button key={status} size="sm" variant={status === 'discarded' ? 'danger' : 'outline'} onClick={() => setDestination(status)}>{t(`opportunities.move.${status}`)}</Button>)}
                    {item.can_delete && <Button variant="danger" icon="trash" onClick={() => setDeleting(true)}>{t('opportunities.delete')}</Button>}
                </div>
                <Tabs tabs={['summary', 'versions', 'documents', 'history'].map(key => ({ id: key, label: t(`opportunities.tabs.${key}`) }))} activeTab={tab} onChange={setTab} />
                {tab === 'summary' && <OpportunitySummary item={item} catalogs={catalogs} />}
                {tab === 'versions' && <OpportunityVersions item={item} onSaved={onSaved} />}
                {tab === 'documents' && <OpportunityDocuments item={item} onSaved={onSaved} />}
                {tab === 'history' && <OpportunityHistory item={item} />}
                {destination && <OpportunityTransition key={`${destination}:${item.record_version}`} item={item} destination={destination} onClose={() => setDestination(null)} onSaved={onSaved} />}
                <ModalDelete isOpen={deleting} onClose={() => setDeleting(false)} itemName={item.title} badgeText={item.code} onConfirm={() => { void remove(); }} isDeleting={busy} description={t('opportunities.delete_explanation')}>
                    {error && <Alert variant="error">{error}</Alert>}<Textarea label={t('opportunities.optional_reason')} value={reason} onChange={event => setReason(event.target.value)} />
                </ModalDelete>
            </>}
        </div>
    </Modal>;
};
